import React from 'react';
import { Box, Typography, Backdrop, Paper } from '@mui/material';
import Loader from '../../Loader';

const LoaderWrapper = ({ 
  loading = false, 
  children, 
  type = 'overlay', // 'overlay', 'inline', 'fullscreen', 'page', 'button'
  message = '',
  size = 'medium', // 'small', 'medium', 'large'
  transparent = false,
  ...props 
}) => {
  if (!loading && !children) return null;

  const getLoaderSize = () => {
    switch (size) {
      case 'small':
        return { transform: 'scale(0.6)' };
      case 'large':
        return { transform: 'scale(1.4)' };
      default:
        return { transform: 'scale(1)' };
    }
  };

  const renderLoader = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        ...getLoaderSize(),
      }}
    >
      <Loader />
      {message && (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            mt: 2,
            textAlign: 'center',
            fontSize: size === 'small' ? '0.8rem' : 'inherit'
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  // Full screen overlay (for initial app loading)
  if (type === 'fullscreen') {
    return (
      <Backdrop 
        open={loading} 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: transparent ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.7)',
          flexDirection: 'column'
        }}
        {...props}
      >
        {renderLoader()}
      </Backdrop>
    );
  }

  // Page-level loading (replaces LinearProgress in layouts)
  if (type === 'page') {
    if (!loading) return children;
    return (
      <Box sx={{ 
        width: '100%', 
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4
      }}>
        {renderLoader()}
      </Box>
    );
  }

  // Inline loading (for small sections)
  if (type === 'inline') {
    if (!loading) return children;
    return (
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        minHeight: '100px'
      }}>
        {renderLoader()}
      </Box>
    );
  }

  // Button loading (for form submissions)
  if (type === 'button') {
    return (
      <Box sx={{ 
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1
      }}>
        {loading && (
          <Box sx={{ transform: 'scale(0.4)' }}>
            <Loader />
          </Box>
        )}
        {children}
      </Box>
    );
  }

  // Overlay loading (default - overlays content)
  return (
    <Box sx={{ position: 'relative', ...props.sx }} {...props}>
      {children}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: transparent ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: 'inherit'
          }}
        >
          {renderLoader()}
        </Box>
      )}
    </Box>
  );
};

export default LoaderWrapper;